import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectProps } from '@mui/material/Select'
import React from 'react'
import { Colors, FontWeight } from '../../constants/theme'

const ITEM_HEIGHT = 36
const ITEM_PADDING_TOP = 8
const MenuProps = {
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
        width: 200,
        borderRadius: 12,
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        border: `1px solid ${Colors.grey400}`,
        marginTop: 4
      }
    }
  }
}

const StyledSelect = (props: SelectProps) => {
  const { className, ...rest } = props
  return (
    <Select
      className={`text-[#30465F] bg-[#FFFFFF] rounded-full! h-[36px]! min-h-[36px]! border! border-solid! border-[#D1E9F4]! transition-all duration-200 ease-linear [&.MuiOutlinedInput-root]:rounded-full! [&.MuiOutlinedInput-root_.MuiOutlinedInput-notchedOutline]:border-none! [&_.MuiSelect-select]:py-[6px]! [&_.MuiSelect-select]:pr-[12px]! [&_.MuiSelect-select]:pl-[14px]! [&_.MuiSelect-select]:text-[13px]! [&_.MuiSelect-select]:font-medium [&_.MuiSelect-select]:min-h-0! [&_.MuiSelect-select]:h-auto! [&_.MuiSelect-select]:flex! [&_.MuiSelect-select]:items-center [&_.MuiSelect-select]:whitespace-nowrap [&_.MuiSelect-select]:overflow-hidden [&_.MuiSelect-select]:text-ellipsis [&_.MuiSelect-select]:leading-[1.2]! [&_.MuiSelect-icon]:text-[14px] [&_.MuiSelect-icon]:right-[10px] ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

interface CustomFilterDropdownProps {
  // Stable ID required - see CustomFilterMultiSelect.tsx for the React 19
  // useId() drift rationale. CLAUDE.md "Trigger K".
  id: string
  value: string | undefined
  onChange: (value: string) => void
  options: Record<string, string>
  placeholder: string
  icon: string
  isActive: boolean
  size?: 'small' | 'medium'
}

const CustomFilterDropdown: React.FC<CustomFilterDropdownProps> = ({
  id,
  value,
  onChange,
  options,
  placeholder,
  icon,
  isActive,
  size = 'small'
}) => {
  const getDisplayText = () => {
    if (value && value !== '') {
      const selectedText = options[value]
      return selectedText
    }
    return placeholder
  }

  return (
    <FormControl size={size}>
      <StyledSelect
        id={id}
        labelId={`${id}-label`}
        inputProps={{ id: `${id}-input` }}
        value={value || ''}
        onChange={(event) => onChange(event.target.value as string)}
        displayEmpty
        MenuProps={MenuProps}
        sx={{
          border: isActive
            ? `1px solid ${Colors.blue800}66 !important`
            : `1px solid ${Colors.grey400} !important`,
          backgroundColor: isActive
            ? `${Colors.blue500}10 !important`
            : `${Colors.white500} !important`,
          '&:hover': {
            borderColor: `${Colors.black500}66 !important`
          },
          '& .MuiSelect-select': {
            // blue800, not blue500: blue500 text is 2.7:1 on the tint.
            color: isActive ? Colors.blue800 : Colors.black700,
            fontWeight: isActive ? FontWeight.semiBold : FontWeight.medium
          },
          '& .MuiSelect-icon': {
            color: isActive ? Colors.blue800 : Colors.black400
          }
        }}
        renderValue={() => (
          <div className="flex w-full items-center gap-1.5 pr-3">
            <i
              className={`si ${icon} shrink-0 text-xs ${isActive ? 'text-blue-800' : 'text-ink-200'}`}
            />
            <span className="flex-1 truncate text-[13px]">
              {getDisplayText()}
            </span>
          </div>
        )}
      >
        {Object.entries(options).map(([key, label]) => {
          const isSelected = value === key

          return (
            <MenuItem
              key={key}
              value={key}
              sx={{
                minHeight: ITEM_HEIGHT,
                padding: '8px 14px',
                fontSize: '13px',
                color: isSelected ? Colors.blue800 : Colors.black700,
                fontWeight: isSelected
                  ? FontWeight.semiBold
                  : FontWeight.medium,
                backgroundColor: isSelected
                  ? `${Colors.blue500}10`
                  : 'transparent',
                margin: '1px 6px',
                borderRadius: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': {
                  backgroundColor: isSelected
                    ? `${Colors.blue500}15`
                    : Colors.grey300
                },
                '&.Mui-selected': {
                  backgroundColor: `${Colors.blue500}10`,
                  color: Colors.blue800,
                  '&:hover': {
                    backgroundColor: `${Colors.blue500}15`
                  }
                },
                '&:first-of-type': {
                  marginTop: '4px'
                },
                '&:last-of-type': {
                  marginBottom: '4px'
                }
              }}
            >
              {label}
            </MenuItem>
          )
        })}
      </StyledSelect>
    </FormControl>
  )
}

export default CustomFilterDropdown
